import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Tabs } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import i18n from 'i18n'
import dataManager from 'pages/main/data/DataManager'
import settings from 'popups/Settings'
import type { SendStatusResponse } from 'services/read-receipt'
import { timeFormat } from 'utils'

import { subscribeReadReceipt } from '../readReceiptListenerStore'

import styles from './style.module.scss'

interface Props {
  emailId: string
  initialStatus: SendStatusResponse
}

type Recipient = SendStatusResponse['recipients'][number]

/**
 * Read Receipt 상세 팝업의 본문. (host 컨텍스트 — 5차 보완으로 viewer 에서 이동)
 * Figma 79988-2328 구조: 헤더 / status / 탭 / 수신자 목록.
 *
 * 탭은 antd Tabs (3개: All / Read / Unread). 각 탭마다 필터링된 수신자 목록 표시.
 *
 * emailId 를 readReceiptListenerStore 에 구독해 refresh 결과를 자동 반영.
 * - notify(status=null) → 갱신 시작(로딩 인디케이터 표시)
 * - notify(status=SendStatusResponse) → 본문 데이터 갱신
 */
export default function ReadReceiptDetailContent({ emailId, initialStatus }: Props) {
  const [status, setStatus] = useState<SendStatusResponse>(initialStatus)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

  useEffect(() => {
    const unsubscribe = subscribeReadReceipt(emailId, (newStatus) => {
      if (newStatus == null) {
        // 갱신 시작 신호 — 본문은 그대로 두고 로딩 인디케이터만 노출.
        setIsRefreshing(true)
        return
      }
      setStatus(newStatus)
      setIsRefreshing(false)
    })
    return unsubscribe
  }, [emailId])

  const percentageText = `${status.read}/${status.total}`
  const percentLabel = `${i18n.t('readReceipt.popup.readPercent', {
    percent: Math.round(status.read_percentage),
  })}`
  const progressPercent =
    status.total > 0 ? Math.min(100, Math.max(0, (status.read / status.total) * 100)) : 0
  const byTypeText = formatByTypeText(status)

  const readRecipients = status.recipients.filter((r) => r.status === 'read')
  const unreadRecipients = status.recipients.filter((r) => r.status !== 'read')

  const tabItems = [
    {
      key: 'all',
      label: i18n.t('readReceipt.tab.all', { count: status.total }),
      children: <RecipientList recipients={status.recipients} />,
    },
    {
      key: 'read',
      label: i18n.t('readReceipt.tab.read', { count: status.read }),
      children: <RecipientList recipients={readRecipients} />,
    },
    {
      key: 'unread',
      label: i18n.t('readReceipt.tab.unread', { count: status.unread }),
      children: <RecipientList recipients={unreadRecipients} />,
    },
  ]

  return (
    <div className={classNames(styles.container)}>
      <header className={classNames(styles.header)}>
        <div className={classNames(styles.titleArea)}>
          <h2 className={classNames(styles.title)}>{i18n.t('readReceipt.popup.title')}</h2>
          <p className={classNames(styles.sentAt)}>
            {i18n.t('readReceipt.popup.sentAt', { time: formatDateTime(status.sent_at) })}
          </p>
        </div>
      </header>

      <section className={classNames(styles.statusSection)}>
        <div className={classNames(styles.statusLine)}>
          <span className={classNames(styles.percentage)}>{percentageText}</span>
          <span className={classNames(styles.percentLabel)}>{percentLabel}</span>
          {isRefreshing && (
            <span className={classNames(styles.refreshingIndicator)} aria-live="polite">
              <LoadingOutlined spin />
            </span>
          )}
        </div>
        <div className={classNames(styles.progressBar)}>
          <div
            className={classNames(styles.progressFill)}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {byTypeText.length > 0 && <p className={classNames(styles.byType)}>{byTypeText}</p>}
      </section>

      <Tabs
        defaultActiveKey="all"
        items={tabItems}
        className={classNames(styles.tabs)}
        destroyOnHidden
      />
    </div>
  )
}

function RecipientAvatar({ email, name }: { email: string; name?: string | null }) {
  const [avatar, setAvatar] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // host 측에서는 viewerExt.NineNative.getAvatar 의 내부 구현과 동일하게 직접 조회
    dataManager
      .getAddressObj(email, name ?? '')
      .getInfoForAvatar()
      .then((info: any) => {
        if (cancelled || info == null) return
        if (info.avatarUrl != null) {
          setAvatar(info.avatarUrl)
        }
      })
      .catch(() => {
        // 아바타 로드 실패 시 placeholder 유지
      })
    return () => {
      cancelled = true
    }
  }, [email, name])

  if (avatar == null) {
    return <div className={classNames(styles.avatar, styles.avatarPlaceholder)} aria-hidden />
  }
  return <img src={avatar} className={classNames(styles.avatar)} alt="" width={32} height={32} />
}

function RecipientList({ recipients }: { recipients: Recipient[] }) {
  return (
    <ul className={classNames(styles.recipientList)}>
      {recipients.map((r) => (
        <li key={r.receipt_id} className={classNames(styles.recipientItem)}>
          <RecipientAvatar email={r.recipient_email} name={r.recipient_display_name} />
          <div className={classNames(styles.recipientInfo)}>
            <div className={classNames(styles.nameLine)}>
              <span className={classNames(styles.name)}>
                {r.recipient_display_name || r.recipient_email}
              </span>
              <span className={classNames(styles.typeBadge)}>
                {/* i18next-extract-disable-next-line */}
                {i18n.t(`email.field.${r.recipient_type}`).toUpperCase()}
              </span>
            </div>
            <p className={classNames(styles.email)}>{r.recipient_email}</p>
          </div>
          <div className={classNames(styles.recipientStatus)}>
            {r.status === 'read' ? (
              <>
                <span className={classNames(styles.statusLabel)}>
                  {i18n.t('readReceipt.recipient.read')}
                </span>
                {r.first_read_real_at != null && (
                  <span className={classNames(styles.statusTime)}>
                    {formatShortDateTime(r.first_read_real_at)}
                  </span>
                )}
              </>
            ) : (
              <span className={classNames(styles.statusLabel)}>
                {i18n.t('readReceipt.recipient.unread')}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

function formatByTypeText(status: SendStatusResponse): string {
  const parts: string[] = []
  const { to, cc, bcc } = status.by_type
  if (to != null) parts.push(`${i18n.t('email.field.to')} ${to.read}/${to.total}`)
  if (cc != null) parts.push(`${i18n.t('email.field.cc')} ${cc.read}/${cc.total}`)
  if (bcc != null) {
    parts.push(`${i18n.t('email.field.bcc')} ${bcc.read}/${bcc.total}`)
  }
  return parts.join(' | ')
}

function formatDateTime(iso: string): string {
  const { dateTimeFormat } = settings.getTimeFormat()
  return timeFormat(iso, dateTimeFormat)
}

function formatShortDateTime(iso: string): string {
  return timeFormat(iso, 'MM-DD HH:mm')
}
